import { useEffect, useRef, useState } from "react";
import { Copy, Flag, ImageDown, Link2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { BillStatus, ShareTone } from "@boo/shared";
import type { PaymentProfile } from "@boo/shared";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/ConfirmModal";
import {
  fetchPaymentProfiles,
  fetchProfileQrUrl,
} from "@/services/paymentProfileApi";
import { useSplitGroupStore } from "@/stores/useSplitGroupStore";
import { useBillStore } from "../store/useBillStore";
import { billTotal } from "../lib/billMath";
import {
  buildShareMessage,
  matchGroupId,
  toTemplate,
} from "../lib/shareMessage";
import { shareNodeAsImage } from "../lib/exportImage";
import { INPUT_CLASS, type BillEditor } from "../types/bills.types";
import ShareImageCard from "./ShareImageCard";
import { Link } from "react-router-dom";

const shareUrl = (token: string) => `${window.location.origin}/b/${token}`;

const message = (err: unknown) =>
  err instanceof Error ? err.message : "Something went wrong";

export default function SharePanel({ editor }: { editor: BillEditor }) {
  const [profiles, setProfiles] = useState<PaymentProfile[]>([]);
  const [fetchedQr, setFetchedQr] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [confirmingNewLink, setConfirmingNewLink] = useState(false);
  const [groupChoice, setGroupChoice] = useState<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const { groups, loadGroups, updateGroup } = useSplitGroupStore();
  const cardRef = useRef<HTMLDivElement>(null);
  const { setPaymentProfile, createShare, revokeShare, resolveFlag } =
    useBillStore.getState();

  const finalized = editor.status === BillStatus.Finalized;
  const profile =
    profiles.find((p) => p.id === editor.paymentProfileId) ?? null;
  const link = editor.share ? shareUrl(editor.share.token) : null;

  const groupId =
    groupChoice ??
    matchGroupId(
      editor.people.flatMap((p) => (p.friendId ? [p.friendId] : [])),
      groups,
    );
  const group = groups.find((g) => g.id === groupId);
  const tone = group?.tone ?? ShareTone.Neutral;
  const messageValues = {
    title: editor.title || "Meal",
    total: billTotal(editor.items, editor.charges),
    link: link ?? "",
  };
  const shareText = link
    ? (draft ??
      buildShareMessage({
        ...messageValues,
        finalized,
        tone,
        template: finalized
          ? group?.shareMessageFinal
          : group?.shareMessageOpen,
      }))
    : "";

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    async function loadProfiles() {
      try {
        setProfiles(await fetchPaymentProfiles());
      } catch {
        toast.error("Failed to load payment profiles");
      }
    }
    void loadProfiles();
  }, []);

  const qrProfileId = profile?.hasQr ? profile.id : null;
  useEffect(() => {
    if (!qrProfileId) return;
    let url: string | null = null;
    let cancelled = false;
    async function loadQr() {
      try {
        const u = await fetchProfileQrUrl(qrProfileId!);
        url = u;
        if (cancelled) URL.revokeObjectURL(u);
        else setFetchedQr(u);
      } catch {
        setFetchedQr(null);
      }
    }
    void loadQr();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [qrProfileId]);
  const qrUrl = qrProfileId ? fetchedQr : null;

  async function run(fn: () => Promise<unknown>, ok?: string) {
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(message(err));
    }
  }

  async function copyLink() {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    toast.success("Link copied");
  }

  async function copyMessage() {
    if (!link) return;
    await navigator.clipboard.writeText(shareText);
    toast.success("Message copied");
  }

  async function saveWording() {
    if (!group || draft === null) return;
    try {
      await updateGroup(group.id, {
        [finalized ? "shareMessageFinal" : "shareMessageOpen"]: toTemplate(
          draft,
          messageValues,
        ),
      });
      setDraft(null);
      toast.success(`Saved wording for ${group.name}`);
    } catch (err) {
      toast.error(message(err));
    }
  }

  async function saveImage() {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      await shareNodeAsImage(cardRef.current, `${editor.title || "bill"}.png`);
    } catch (err) {
      toast.error(message(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">
        Share with friends
      </h2>

      <label className="flex flex-col gap-2 text-xs text-muted-foreground">
        Pay-back details
        {profiles.length === 0 ? (
          <div>
            No payment details yet.{" "}
            <Link
              to="/settings?tab=payment-details"
              className="text-primary underline"
            >
              Add your QR / account
            </Link>
          </div>
        ) : (
          <select
            value={editor.paymentProfileId ?? ""}
            onChange={(e) =>
              run(() => setPaymentProfile(e.target.value || null))
            }
            className={`${INPUT_CLASS} w-full py-2`}
          >
            <option value="">None</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </label>

      {link ? (
        <div className="space-y-2">
          <div className="break-all rounded-lg bg-[var(--surface-muted)] px-3 py-2 text-xs text-foreground">
            {link}
          </div>
          <label className="flex flex-col gap-2 text-xs text-muted-foreground">
            Message for
            <select
              value={groupId}
              onChange={(e) => {
                setGroupChoice(e.target.value);
                setDraft(null);
              }}
              className={`${INPUT_CLASS} w-full py-2`}
            >
              <option value="">Anyone (neutral)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.tone === ShareTone.Playful ? " · playful" : ""}
                </option>
              ))}
            </select>
          </label>
          <textarea
            value={shareText}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            className={`${INPUT_CLASS} w-full resize-y py-2 text-xs`}
          />
          {group && draft !== null && (
            <Button size="sm" variant="outline" onClick={saveWording}>
              Save wording to {group.name}
            </Button>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={copyLink}>
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
            </Button>
            <Button size="sm" variant="outline" onClick={copyMessage}>
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Copy message
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmingNewLink(true)}
            >
              New link
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => run(revokeShare, "Link disabled")}
            >
              Disable
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" onClick={() => run(createShare)}>
          <Link2 className="w-3.5 h-3.5 mr-1.5" /> Create share link
        </Button>
      )}

      {finalized && (
        <div>
          <Button
            size="sm"
            variant="outline"
            loading={exporting}
            onClick={saveImage}
          >
            {!exporting && <ImageDown className="w-3.5 h-3.5 mr-1.5" />}
            {exporting ? "Rendering…" : "Save as image"}
          </Button>
          <ShareImageCard
            ref={cardRef}
            editor={editor}
            profile={profile}
            qrUrl={qrUrl}
            link={link}
          />
        </div>
      )}

      {editor.flags.length > 0 && (
        <div className="space-y-2">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <Flag className="w-3.5 h-3.5 text-destructive" />
            Flagged by friends
          </h3>
          <ul className="space-y-1.5">
            {editor.flags.map((f) => {
              const item = editor.items.find((i) => i.key === f.itemId);
              return (
                <li
                  key={f.id}
                  className="flex items-start justify-between gap-2 rounded-lg border border-border px-3 py-2 text-xs"
                >
                  <div className="min-w-0 break-words">
                    <div className="font-medium text-foreground">
                      {item?.name ?? "Item"} · {f.personName}
                    </div>
                    <div className="text-muted-foreground">{f.note}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => run(() => resolveFlag(f.id))}
                    className="-my-2 -mr-2 shrink-0 px-3 py-3 text-primary active:opacity-60"
                  >
                    Resolve
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <ConfirmModal
        open={confirmingNewLink}
        title="Create a new link?"
        message="The old link stops working."
        confirmLabel="Create new link"
        onConfirm={() => {
          setConfirmingNewLink(false);
          void run(createShare, "New link created");
        }}
        onCancel={() => setConfirmingNewLink(false)}
      />
    </section>
  );
}
